import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { MelioIntelligenceContext, MelioIntelligenceItem, PersonalizedMelioIntelligenceItem } from '../types/melioIntelligence';
import { MyInputsCurrentProfile } from '../types/myInputs';

const EXPLANATION_LABELS: Record<string, string> = {
  adderall: 'Relevant because you take Adderall',
  amphetamine_stimulants: 'Relevant to stimulant users',
  fish_intake_high: 'Relevant because you report frequent oily fish intake',
  omega_3_supplement: 'Relevant to your omega-3 supplementation',
  iron_supplement: 'Relevant because you take an iron supplement',
  glp1: 'Relevant to GLP-1 users',
  statin: 'Relevant to statin users',
  high_caffeine: 'Relevant because you report high caffeine intake',
  alcohol_weekly: 'Relevant because you report weekly alcohol intake',
  apob_focus: 'Relevant to your lipid and ApoB focus',
  sleep_focus: 'Relevant to your plan’s sleep and recovery focus',
  glucose_focus: 'Relevant to your glucose-focused plan',
  cardiovascular_risk: 'Relevant to your cardiovascular prevention focus',
  family_cancer_risk: 'Relevant to your family cancer-risk context',
};

function normalizeText(value?: string | null) {
  return value?.trim().toLowerCase() || '';
}

export async function listPublishedMelioIntelligenceItems(limitCount = 24): Promise<MelioIntelligenceItem[]> {
  const snapshot = await getDocs(
    query(
      collection(db, 'melio_intelligence_items'),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      limit(limitCount),
    ),
  );

  return snapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...(docSnapshot.data() as Omit<MelioIntelligenceItem, 'id'>),
  }));
}

export function deriveInputTriggers(profile: MyInputsCurrentProfile | null): string[] {
  if (!profile) return [];

  const triggers = new Set<string>();

  profile.medications
    .filter((entry) => entry.status === 'current' || entry.status === 'as_needed')
    .forEach((entry) => {
      const name = normalizeText(entry.name);
      if (name.includes('adderall')) triggers.add('adderall');
      if (entry.category === 'stimulant') triggers.add('amphetamine_stimulants');
      if (entry.category === 'glp1') triggers.add('glp1');
      if (entry.category === 'statin') triggers.add('statin');
    });

  profile.supplements
    .filter((entry) => entry.status === 'current' || entry.status === 'occasional')
    .forEach((entry) => {
      if (entry.category === 'omega_3') triggers.add('omega_3_supplement');
      if (entry.category === 'iron') triggers.add('iron_supplement');
    });

  if (profile.dietPatterns.oilyFishFrequency === 'several_times_weekly') triggers.add('fish_intake_high');
  if (profile.dietPatterns.caffeinePattern === 'high') triggers.add('high_caffeine');
  if (profile.dietPatterns.alcoholPattern === 'weekly' || profile.dietPatterns.alcoholPattern === 'frequent') {
    triggers.add('alcohol_weekly');
  }

  return [...triggers];
}

export function derivePlanTags(context: MelioIntelligenceContext): string[] {
  const tags = new Set<string>();
  const summary = normalizeText(context.latestPlan?.summary);
  const patientSummary = normalizeText(context.latestPlan?.patientSummary);
  const familySummary = normalizeText(context.latestPlan?.familyHistorySummary);

  if (summary.includes('sleep') || patientSummary.includes('sleep')) tags.add('sleep_focus');
  if (summary.includes('apob') || summary.includes('lipid') || patientSummary.includes('apob')) tags.add('apob_focus');
  if (summary.includes('glucose') || summary.includes('hba1c') || patientSummary.includes('glucose')) tags.add('glucose_focus');
  if (summary.includes('cardio') || summary.includes('cardiovascular') || patientSummary.includes('cardiovascular')) tags.add('cardiovascular_risk');
  if (familySummary.includes('cancer') || familySummary.includes('lynch') || familySummary.includes('brca')) tags.add('family_cancer_risk');
  if ((context.familyTree?.insightChips || []).some((chip) => normalizeText(chip.label).includes('maternal pattern') || normalizeText(chip.label).includes('paternal pattern'))) {
    tags.add('family_cancer_risk');
  }
  if (context.openPrompts.some((prompt) => normalizeText(prompt.title).includes('sleep'))) tags.add('sleep_focus');
  if (context.labResults.some((result) => normalizeText(result.biomarker).includes('apob') || normalizeText(result.biomarker).includes('ldl'))) tags.add('apob_focus');

  return [...tags];
}

function explanationForMatch(item: MelioIntelligenceItem, matchedPlanTags: string[], matchedTriggers: string[]) {
  const planMatch = matchedPlanTags.find((tag) => EXPLANATION_LABELS[tag]);
  if (planMatch) return EXPLANATION_LABELS[planMatch];
  const triggerMatch = matchedTriggers.find((tag) => EXPLANATION_LABELS[tag]);
  if (triggerMatch) return EXPLANATION_LABELS[triggerMatch];
  if (item.topicTags.includes('general_prevention')) return null;
  return null;
}

export function personalizeMelioIntelligenceItems(
  items: MelioIntelligenceItem[],
  context: MelioIntelligenceContext,
): PersonalizedMelioIntelligenceItem[] {
  const triggers = deriveInputTriggers(context.myInputsProfile);
  const planTags = derivePlanTags(context);

  const scored = items
    .map((item) => {
      const matchedTriggers = item.inputTriggers.filter((trigger) => triggers.includes(trigger));
      const matchedPlanTags = item.planTags.filter((tag) => planTags.includes(tag));
      const score = matchedPlanTags.length * 10 + matchedTriggers.length * 8 + Math.min(item.topicTags.length, 3);

      return {
        ...item,
        personalizationScore: score,
        personalizationReason: explanationForMatch(item, matchedPlanTags, matchedTriggers),
        matchedTriggers,
        matchedPlanTags,
      };
    })
    .sort((left, right) => {
      if (right.personalizationScore !== left.personalizationScore) {
        return right.personalizationScore - left.personalizationScore;
      }
      const leftTime = ((left.publishedAt as { toDate?: () => Date })?.toDate?.() ?? new Date(left.publishedAt as string)).getTime?.() || 0;
      const rightTime = ((right.publishedAt as { toDate?: () => Date })?.toDate?.() ?? new Date(right.publishedAt as string)).getTime?.() || 0;
      return rightTime - leftTime;
    });

  let personalizedCount = 0;
  return scored.map((item) => {
    if (!item.personalizationReason) return item;
    if (personalizedCount >= 2) {
      return {
        ...item,
        personalizationReason: null,
      };
    }
    personalizedCount += 1;
    return item;
  });
}

export async function getPersonalizedMelioIntelligenceFeed(context: MelioIntelligenceContext) {
  const items = await listPublishedMelioIntelligenceItems();
  return personalizeMelioIntelligenceItems(items, context);
}
